import { Formik, Field } from 'formik';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { Trans, useTranslation } from 'react-i18next';

import { IContentTypeValue } from 'graphql/types';
import { ExtraCategoryFormFields } from 'organisation/components/ExtraCategoryFormFields';
import { TCourseFormValues } from 'training/types';
import { Divider } from 'common/components/Divider';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { FormSection } from 'common/components/FormSection';
import { Link } from 'common/components/Link';
import { ClipboardCopyInput, ImageField } from 'common/components/FormField';
import { Button } from 'common/components/Button';
import { CARD_IMAGE_MAX_SIZE, CARD_IMAGE_TYPE } from 'common/constants/files';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';
import { Banner } from 'common/components/Banner';
import { Img } from 'common/components/Img';
import ModuleCardImage from 'assets/images/module-card-image.webp';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';

interface IProps {
    disabled?: boolean;
    isEdit?: boolean;
    initialValues: TCourseFormValues;
    onClose?(): void;
    onDelete?(): void;
    onSubmit(values: TCourseFormValues): void;
}

export const CourseForm = ({
    disabled,
    isEdit,
    onClose,
    onDelete,
    onSubmit,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const smallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.only('xs')
    );

    return (
        <Formik enableReinitialize onSubmit={onSubmit} {...other}>
            {({ submitForm, isSubmitting, dirty }) => (
                <>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />
                    <Box sx={{ mb: 3 }}>
                        <Field
                            component={ClipboardCopyInput}
                            label={translate('courseForm.uniqueId')}
                            name="id"
                        />
                    </Box>

                    <Divider />

                    <FormSection
                        description={
                            <Trans
                                components={{
                                    a: (
                                        <Link
                                            rel="noreferrer"
                                            underline="always"
                                        />
                                    ),
                                }}
                                i18nKey="courseForm.imageDescription"
                            />
                        }
                        title={translate('image')}
                    >
                        <Stack
                            direction={smallScreen ? 'column-reverse' : 'row'}
                            spacing={2}
                            sx={{ mt: 2 }}
                        >
                            <Box sx={{ width: '100%' }}>
                                <Field
                                    acceptedFileTypes={CARD_IMAGE_TYPE}
                                    component={ImageField}
                                    helperText={translate(
                                        'imageSizeDescription',
                                        {
                                            width: IMAGE_VARIANTS[
                                                EImageVariant.Card
                                            ].minWidth,
                                            height: IMAGE_VARIANTS[
                                                EImageVariant.Card
                                            ].minHeight,
                                        }
                                    )}
                                    imageVariant={EImageVariant.Card}
                                    label={translate('image')}
                                    maxUploadSize={CARD_IMAGE_MAX_SIZE}
                                    name="thumbnail"
                                />
                            </Box>
                            <Box sx={{ width: '100%' }}>
                                <Banner>
                                    <Box sx={{ py: 1, pr: 1, pl: 2 }}>
                                        {translate(
                                            'courseForm.imageBannerDescription'
                                        )}
                                    </Box>
                                    <Box sx={{ mt: 'auto' }}>
                                        <Img src={ModuleCardImage} />
                                    </Box>
                                </Banner>
                            </Box>
                        </Stack>
                    </FormSection>

                    <FormSection
                        description={
                            <Box>
                                <Trans
                                    components={{
                                        a: (
                                            <Link
                                                rel="noreferrer"
                                                underline="always"
                                            />
                                        ),
                                    }}
                                    i18nKey="courseForm.categoriesDescription"
                                />
                            </Box>
                        }
                        title={translate('courseForm.categories')}
                    >
                        <Box sx={{ mt: 2 }}>
                            <ExtraCategoryFormFields
                                contentType={IContentTypeValue.Course}
                            />
                        </Box>
                    </FormSection>

                    <FormSection
                        description={
                            <Box>
                                <Trans
                                    components={{
                                        a: (
                                            <Link
                                                rel="noreferrer"
                                                underline="always"
                                            />
                                        ),
                                    }}
                                    i18nKey="courseForm.lti1_1Description"
                                />
                            </Box>
                        }
                        title={translate('courseForm.lti1_1')}
                    >
                        <Box sx={{ mt: 2 }}>
                            <Field
                                component={ClipboardCopyInput}
                                label={translate('courseForm.ltiLaunchUrl')}
                                name="ltiLaunchUrl"
                            />
                            <Field
                                component={ClipboardCopyInput}
                                label={translate('courseForm.ltiKey')}
                                name="ltiKey"
                            />
                            <Field
                                component={ClipboardCopyInput}
                                label={translate('courseForm.ltiSecret')}
                                name="ltiSecret"
                            />
                        </Box>
                    </FormSection>

                    <Box sx={{ display: 'flex', mt: 4 }}>
                        <Button
                            color="primary"
                            disabled={
                                isSubmitting ||
                                disabled ||
                                (!isSubmitting && !disabled && !dirty)
                            }
                            loading={isSubmitting}
                            type="submit"
                            variant="contained"
                            onClick={(
                                e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                                e.preventDefault();

                                return submitForm();
                            }}
                        >
                            {translate('courseForm.editButton')}
                        </Button>

                        {onClose && (
                            <Box sx={{ ml: 2 }}>
                                <Button
                                    disabled={isSubmitting}
                                    variant="contained"
                                    onClick={onClose}
                                >
                                    {translate('cancel')}
                                </Button>
                            </Box>
                        )}

                        <Box sx={{ ml: 'auto' }}>
                            <FormDeleteButton
                                disabled={isSubmitting}
                                onClick={() => onDelete?.()}
                            >
                                {translate('delete')}
                            </FormDeleteButton>
                        </Box>
                    </Box>
                </>
            )}
        </Formik>
    );
};
